import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Link from "@material-ui/core/Link";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";

import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { Helmet } from "react-helmet";
import {
  TextField,
  InputAdornment,
  IconButton,
  makeStyles,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";

import "./wallpaper.css";

import {
  Email,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    flex: 2,
    position: "relative",
    background:
      theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },
  rightScreen: {
    flex: 1,
    width: "100vw",
    height: "100vh",
    background:
      theme.mode === "light" ? theme.palette.light : theme.palette.dark,
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  leftScreen: {
    flex: 2,
    [theme.breakpoints.down("sm", "xs")]: {
      display: "none",
    },
  },
  paper: {
    backgroundColor:
      theme.mode === "light"
        ? "rgba(255, 255, 255, 0.8)"
        : "rgba(0, 0, 0, 0.8)",
    backdropFilter: "blur(5px)",
    boxShadow:
      theme.mode === "light"
        ? "0 4px 6px rgba(0, 0, 0, 0.2)"
        : "0 4px 6px rgba(0, 0, 0, 0.5)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "25px",
    borderRadius: "12px",
    maxWidth: "400px", // Limita a largura máxima do container
    width: "100%", // A largura será 100% até o máximo definido
  },
  signupButton: {
    marginTop: "45px",
    alignSelf: "center",
    borderRadius: "50px",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
    borderRadius: "12px",
  },
  submit: {
    margin: "0px 0px -65px 0px",
    color: "white",
    borderRadius: "50px",
    width: "100%",
    [theme.breakpoints.up('sm')]: { 
      width: "250px",
    },
  },
  powered: {
    color: "white",
  },
  logoImg: {
    width: "100%",
    maxWidth: "250px",
    height: "auto",
    maxHeight: "120px",
    margin: "0 auto",
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
  },
  iconButton: {
    position: "absolute",
    top: 10,
    right: 10,
    color: theme.mode === "light" ? "black" : "white",
  },
  link: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.mode === "light" ? "black" : "white",
  },
}));

const Login = () => {
  const classes = useStyles();

  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, mode } = colorMode;
  const [user, setUser] = useState({ email: "", password: "" });
  const [allowSignup, setAllowSignup] = useState(false);
  const { getPublicSetting } = useSettings();
  const { handleLogin } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const handleChangeInput = (e) => {
    if (rememberMe) {
      localStorage.setItem("email", user.email);
      localStorage.setItem("password", e.target.value);
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");

    if (savedEmail && savedPassword) {
      setUser({
        email: savedEmail,
        password: savedPassword,
      });

      setRememberMe(true);
    }
  }, []);
  const handleChangeCheck = (e) => {
    setRememberMe(e);
    if (e) {
      localStorage.setItem("email", user.email);
      localStorage.setItem("password", user.password);
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  useEffect(() => {
    getPublicSetting("allowSignup")
      .then((data) => {
        setAllowSignup(data === "enabled");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
  }, []);

  return (
    <>
      <Helmet>
        <title>{appName || "TSA Inteligência"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>
      <div className={classes.rightScreen}>
        <CssBaseline />
        <div className={classes.paper}>
          <IconButton
            className={classes.iconButton}
            onClick={colorMode.toggleColorMode}
          >
            {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          <div>
            <img className={classes.logoImg} alt="logo" />
          </div>
          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ paddingInline: 10 }}
                  >
                    <Email color="inherit" />
                  </InputAdornment>
                ),
              }}
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      id="check"
                      checked={rememberMe}
                      onChange={(e) => handleChangeCheck(e.target.checked)}
                    />
                  }
                  label="Lembrar Usuário"
                />
              </div>
              {/* <Link
                href="#"
                variant="body2"
                component={RouterLink}
                to="/recovery-password"
                className={classes.link}
              >
                {i18n.t("Recuperar Senha?")}
              </Link> */}
            </div>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <span>{i18n.t("login.buttons.submit")}</span>
              </div>
            </Button>
          </form>
        </div>
        <Button
          variant="contained"
          color="primary"
          className={classes.signupButton}
          component={RouterLink}
          to="/signup"
        >
          {i18n.t("Cadastre-se")}
        </Button>
      </div>
    </>
  );
};

export default Login;